import { IMenuItem } from "../global/Sidebar/MenuItems";
import { SettingName } from "../redux/settings/settingsSlice";

export interface SettingModuleRule {
  settingName: SettingName | string;
  flagKey: string;
  expectedValue?: any;
  blockedRoutes: string[];
}

/**
 * Central configuration of setting-based module access rules.
 * To add a new setting-based module rule in the future, simply add a new entry here.
 */
export const SETTING_MODULE_RULES: SettingModuleRule[] = [
  {
    settingName: SettingName.ITEM_PAGE_SETTINGS,
    flagKey: "OrderingModuleEnabled",
    expectedValue: true,
    blockedRoutes: [
      "/vendor-master",
      "/view-vendor",
      "/add-vendor",
      "/edit-vendor",
      "/ordering-product-master",
      "/view-ordering-product",
      "/add-ordering-product",
      "/edit-ordering-product",
      "/mckesson-order-processor",
      "/mckesson-items-master",
    ],
  }
];

/**
 * Parses the raw settings array from the API into a dictionary of { [settingName]: parsedValue }.
 */
export const parseSystemSettings = (rawSettings: any[]): Record<string, any> => {
  const result: Record<string, any> = {};
  if (!Array.isArray(rawSettings)) return result;

  for (const item of rawSettings) {
    if (item?.settingName) {
      if (item.settingValue) {
        try {
          result[item.settingName] = typeof item.settingValue === "string" ? JSON.parse(item.settingValue) : item.settingValue;
        } catch {
          result[item.settingName] = item.settingValue;
        }
      } else {
        result[item.settingName] = null;
      }
    }
  }
  return result;
};

/**
 * Evaluates all setting module rules against current system settings
 * and returns a Set of all currently blocked routes.
 */
export const getBlockedRoutes = (
  systemSettings?: Record<string, any>
): Set<string> => {
  const settings = systemSettings || {};
  const blocked = new Set<string>();

  for (const rule of SETTING_MODULE_RULES) {
    const settingObj = settings[rule.settingName] ??
      // Case-insensitive lookup fallback
      Object.entries(settings).find(
        ([key]) => key.toLowerCase() === rule.settingName.toLowerCase()
      )?.[1];

    if (settingObj && typeof settingObj === "object") {
      const flagVal = settingObj[rule.flagKey];
      const expected = rule.expectedValue ?? true;

      // If the flag is explicitly set and does not match expected value (e.g. OrderingModuleEnabled === false)
      if (flagVal !== undefined && flagVal !== expected) {
        rule.blockedRoutes.forEach((route) => blocked.add(route.toLowerCase()));
      }
    }
  }

  return blocked;
};

/**
 * Checks whether a given path is blocked by any setting module rule.
 */
export const isRouteBlockedBySetting = (
  routePath: string,
  systemSettings?: Record<string, any>
): boolean => {
  if (!routePath) return false;
  const blockedRoutes = getBlockedRoutes(systemSettings);
  const normalizedPath = routePath.toLowerCase();

  // Check exact match or parameterized route matching (e.g., /edit-transaction/:id)
  for (const blocked of Array.from(blockedRoutes)) {
    if (blocked === normalizedPath) {
      return true;
    }
    // Handle parameter patterns if present (e.g. /edit-vendor/:id matching /edit-vendor/123)
    if (blocked.includes("/:")) {
      const pattern = new RegExp("^" + blocked.replace(/:[a-zA-Z0-9_]+/g, "[^/]+") + "$");
      if (pattern.test(normalizedPath)) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Recursively filters sidebar menu items by removing items whose route is blocked.
 * Also removes parent menu items whose childMenus become completely empty.
 */
export const filterBlockedMenuItems = (
  menuItemsList: any[],
  blockedRoutes: Set<string>
): any[] => {
  if (!Array.isArray(menuItemsList) || blockedRoutes.size === 0) {
    return menuItemsList ?? [];
  }

  return menuItemsList
    .map((item) => {
      const route = (item.route || item.path || "").toLowerCase();

      // If this item directly points to a blocked route, drop it
      if (route && blockedRoutes.has(route)) {
        return null;
      }

      // If it has children, recursively filter them
      if (Array.isArray(item.childMenus) && item.childMenus.length > 0) {
        const filteredChildren = filterBlockedMenuItems(
          item.childMenus,
          blockedRoutes
        );

        // If all children were blocked and this parent item has no route of its own, drop parent
        if (filteredChildren.length === 0 && !route) {
          return null;
        }

        return {
          ...item,
          childMenus: filteredChildren,
        };
      }

      return item;
    })
    .filter(Boolean);
};
